/**
 * Amorçage.
 *
 * L'ordre compte : on charge l'état local et on affiche l'interface **avant**
 * de toucher au réseau. En magasin, l'application doit être utilisable à la
 * seconde où elle s'ouvre, réseau ou pas.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';

import App from './App.tsx';
import InvalidLink from './components/InvalidLink.tsx';
import { claimToken, householdIdFromToken } from './lib/token.ts';
import { requestPersistence } from './store/db.ts';
import { hydrate } from './store/state.ts';
import { start } from './store/sync.ts';
import { setUpdateAvailable } from './lib/update.ts';
import { initTheme } from './lib/theme.ts';
import './styles.css';

// Avant toute création de racine React : appliquer le thème après le premier
// rendu ferait clignoter l'application dans les couleurs du système.
initTheme();

const root = createRoot(document.getElementById('root') as HTMLElement);

void boot();

async function boot(): Promise<void> {
  const token = claimToken();
  const householdId = token ? householdIdFromToken(token) : null;

  if (!token || !householdId) {
    root.render(
      <StrictMode>
        <InvalidLink />
      </StrictMode>,
    );
    return;
  }

  // Demande au navigateur de ne pas purger les données. Décisif sur iOS.
  void requestPersistence();

  await hydrate(householdId);

  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // Le réseau n'est sollicité qu'une fois l'interface à l'écran.
  start(token);

  registerSW({
    // Mise à jour sur demande et non automatique : recharger la page pendant
    // qu'on fait ses courses serait une mauvaise surprise.
    onNeedRefresh: () => setUpdateAvailable(true),
  });
}
